import { useMemo, useState } from "react";
import type { MatchBlock } from "@shared/lessonBlocks";
import { RichText } from "../RichText";
import { useCheck, CheckFooter, shuffled, type CheckResult } from "../CheckShell";

interface Props {
  block: MatchBlock;
  onResult: (r: CheckResult) => void;
  alreadyDone?: CheckResult | null;
}

const COLORS = [
  "border-sky-400 bg-sky-100 dark:bg-sky-900/40",
  "border-violet-400 bg-violet-100 dark:bg-violet-900/40",
  "border-amber-400 bg-amber-100 dark:bg-amber-900/40",
  "border-emerald-400 bg-emerald-100 dark:bg-emerald-900/40",
  "border-pink-400 bg-pink-100 dark:bg-pink-900/40",
  "border-teal-400 bg-teal-100 dark:bg-teal-900/40",
];

/** Tap something on the left, then its partner on the right. Tap a matched item to undo it. */
export function MatchBlockView({ block, onResult, alreadyDone }: Props) {
  const check = useCheck(onResult, { alreadyDone });
  const rightOrder = useMemo(() => shuffled(block.pairs.map(p => p.id)), [block.id]);
  const [links, setLinks] = useState<Record<string, string>>({}); // leftId -> rightId (pair ids)
  const [activeLeft, setActiveLeft] = useState<string | null>(null);
  const [wrongLefts, setWrongLefts] = useState<Set<string>>(new Set());
  const done = check.locked;

  const rightToLeft = useMemo(() => Object.fromEntries(Object.entries(links).map(([l, r]) => [r, l])), [links]);
  const colorIndex = (leftId: string) => block.pairs.findIndex(p => p.id === leftId);

  const unlink = (leftId: string) => {
    if (done) return;
    if (check.status === "wrong") check.retry();
    setLinks(prev => { const n = { ...prev }; delete n[leftId]; return n; });
    setWrongLefts(prev => { const n = new Set(prev); n.delete(leftId); return n; });
  };
  const tapLeft = (id: string) => {
    if (done) return;
    if (links[id]) { unlink(id); return; }
    setActiveLeft(activeLeft === id ? null : id);
  };
  const tapRight = (rightId: string) => {
    if (done) return;
    if (rightToLeft[rightId]) { unlink(rightToLeft[rightId]); return; }
    if (!activeLeft) return;
    if (check.status === "wrong") check.retry();
    setLinks(prev => ({ ...prev, [activeLeft]: rightId }));
    setWrongLefts(prev => { const n = new Set(prev); n.delete(activeLeft); return n; });
    setActiveLeft(null);
  };
  const allLinked = block.pairs.every(p => links[p.id]);
  const submit = () => {
    const wrong = block.pairs.filter(p => links[p.id] !== p.id).map(p => p.id);
    setWrongLefts(new Set(wrong));
    check.submit(wrong.length === 0);
  };
  const effectiveLinks = done && check.status === "revealed" ? Object.fromEntries(block.pairs.map(p => [p.id, p.id])) : links;
  const effRightToLeft = Object.fromEntries(Object.entries(effectiveLinks).map(([l, r]) => [r, l]));

  const cls = (leftId: string | undefined, isActive: boolean) => {
    if (!leftId) return isActive ? "border-primary bg-primary/10 ring-2 ring-primary/30" : "border-border bg-card hover:border-primary/50";
    if (done) return effectiveLinks[leftId] === leftId ? "border-emerald-400 bg-emerald-50 dark:bg-emerald-900/30" : "border-rose-400 bg-rose-50 dark:bg-rose-900/30";
    if (wrongLefts.has(leftId)) return "border-rose-400 bg-rose-50 dark:bg-rose-900/30";
    return COLORS[colorIndex(leftId) % COLORS.length];
  };

  return (
    <div className="space-y-4">
      <RichText text={block.prompt} className="text-lg md:text-xl font-semibold" />
      <p className="text-sm text-muted-foreground">{activeLeft ? "Now tap its match on the right." : "Tap an item on the left, then its match on the right."}</p>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          {block.pairs.map(p => {
            const linked = !!effectiveLinks[p.id];
            return (
              <button key={p.id} type="button" disabled={done} onClick={() => tapLeft(p.id)}
                className={`w-full text-left rounded-2xl border-2 p-3 text-sm md:text-base font-medium transition-all ${cls(linked ? p.id : undefined, activeLeft === p.id)}`}
                data-testid={`match-left-${p.id}`}>
                <RichText text={p.left} inline />
              </button>
            );
          })}
        </div>
        <div className="space-y-2">
          {rightOrder.map(rid => {
            const pair = block.pairs.find(p => p.id === rid)!;
            const leftId = effRightToLeft[rid];
            return (
              <button key={rid} type="button" disabled={done} onClick={() => tapRight(rid)}
                className={`w-full text-left rounded-2xl border-2 p-3 text-sm md:text-base font-medium transition-all ${cls(leftId, false)} ${!leftId && activeLeft && !done ? "animate-pulse" : ""}`}
                data-testid={`match-right-${rid}`}>
                <RichText text={pair.right} inline />
              </button>
            );
          })}
        </div>
      </div>
      <CheckFooter
        check={check}
        canSubmit={allLinked}
        onSubmit={submit}
        feedbackWrong={wrongLefts.size ? `${wrongLefts.size} ${wrongLefts.size === 1 ? "pair is" : "pairs are"} not right. Tap the red ones to undo them.` : undefined}
        revealAnswer={<p>Each item is now joined to its correct match.</p>}
      />
    </div>
  );
}
